import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Wrench, Plus, User, Mail, Phone, Shield,
  Loader2, X, Pencil, Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsiteTechnician } from '../../../api/src/types/onsite-types.js';

const STATUS_COLORS: Record<string, string> = {
  available:   'bg-green-500/20 text-green-400',
  on_job:      'bg-primary/20 text-primary',
  off_duty:    'bg-muted-foreground/20 text-muted-foreground',
  unavailable: 'bg-red-500/20 text-red-400',
};

interface TechForm {
  name: string;
  email: string;
  phone: string;
  skills: string;
  certifications: string;
  color: string;
  notes: string;
}

const EMPTY_FORM: TechForm = {
  name: '', email: '', phone: '', skills: '', certifications: '', color: '#f97316', notes: '',
};

export default function TechniciansPage() {
  const { orgId, isDispatcher } = useAuth();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechForm>(EMPTY_FORM);

  const { data, isLoading } = useQuery({
    queryKey: ['technicians', orgId],
    queryFn: () => onsiteApiFetch<{ technicians: OnsiteTechnician[] }>('/api/v1/onsite/technicians'),
    enabled: !!orgId,
  });

  const createTech = useMutation({
    mutationFn: (payload: Partial<OnsiteTechnician>) =>
      onsiteApiFetch<{ technician: OnsiteTechnician }>('/api/v1/onsite/technicians', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['technicians'] });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
  });

  const updateTech = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<OnsiteTechnician> }) =>
      onsiteApiFetch<{ technician: OnsiteTechnician }>(`/api/v1/onsite/technicians/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['technicians'] });
      setEditingId(null);
      setForm(EMPTY_FORM);
    },
  });

  const deleteTech = useMutation({
    mutationFn: (id: string) =>
      onsiteApiFetch(`/api/v1/onsite/technicians/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['technicians'] }),
  });

  function startEdit(tech: OnsiteTechnician) {
    setEditingId(tech.id);
    setForm({
      name: tech.name,
      email: tech.email,
      phone: tech.phone ?? '',
      skills: tech.skills.join(', '),
      certifications: tech.certifications.join(', '),
      color: tech.color ?? '#f97316',
      notes: tech.notes ?? '',
    });
    setShowForm(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;

    const payload: Partial<OnsiteTechnician> = {
      name: form.name,
      email: form.email,
      phone: form.phone || undefined,
      skills: form.skills ? form.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      certifications: form.certifications ? form.certifications.split(',').map(s => s.trim()).filter(Boolean) : [],
      color: form.color || undefined,
      notes: form.notes || undefined,
    };

    if (editingId) {
      await updateTech.mutateAsync({ id: editingId, patch: payload });
    } else {
      await createTech.mutateAsync(payload);
    }
  }

  const techs = data?.technicians ?? [];
  const isPending = createTech.isPending || updateTech.isPending;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-foreground">Technicians</h1>
          {isDispatcher && (
            <button
              onClick={() => { setShowForm(v => !v); setEditingId(null); setForm(EMPTY_FORM); }}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Add Technician
            </button>
          )}
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="border-b border-border bg-card/50 p-4">
          <form onSubmit={handleSubmit} className="space-y-3 max-w-2xl">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-foreground">{editingId ? 'Edit Technician' : 'New Technician'}</h2>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="input" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className="input" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Phone</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Skills (comma-separated)</label>
                <input value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} placeholder="Networking, Servers, Printers" className="input" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Certifications (comma-separated)</label>
                <input value={form.certifications} onChange={e => setForm(f => ({ ...f, certifications: e.target.value }))} placeholder="CompTIA A+, CCNA" className="input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Color</label>
                <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} className="h-10 w-20 rounded border border-border bg-secondary p-1" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Notes</label>
                <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="input" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={isPending || !form.name.trim() || !form.email.trim()} className="btn-primary">
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {editingId ? 'Save' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tech list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : techs.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <Wrench className="h-12 w-12" />
            <p className="text-lg font-medium">No technicians yet</p>
            <p className="text-sm">Add your field technicians to start assigning jobs.</p>
            {isDispatcher && (
              <button
                onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); }}
                className="mt-2 flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" /> Add Technician
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {techs.map(tech => (
              <div key={tech.id} className="flex items-center gap-3 px-4 py-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: (tech.color ?? '#f97316') + '33' }}
                >
                  <User className="h-5 w-5" style={{ color: tech.color ?? '#f97316' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-medium text-foreground">{tech.name}</p>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[tech.status] ?? STATUS_COLORS.available}`}>
                      {tech.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{tech.email}</span>
                    {tech.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{tech.phone}</span>}
                  </div>
                  {(tech.skills.length > 0 || tech.certifications.length > 0) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {tech.skills.map(s => (
                        <span key={s} className="rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground">{s}</span>
                      ))}
                      {tech.certifications.map(c => (
                        <span key={c} className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary flex items-center gap-0.5">
                          <Shield className="h-2.5 w-2.5" />{c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {isDispatcher && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => startEdit(tech)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => { if (confirm(`Remove ${tech.name}?`)) deleteTech.mutate(tech.id); }}
                      className="rounded p-1.5 text-muted-foreground hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
